import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { selectUnitsConfig } from '../../../../../services/store/modules/app-controls';
import { TimeSeriesBarChartRecharts } from '../../../../shared/charts/recharts';
import { buildBarChartColors } from '../GraphCards/barChartColors';
import {
  buildWindDirectionLabelRow,
  buildWindSpeedValueLabelRow,
  type EnrichedGraphSeriesPoint,
} from '../../helpers/graph/graphData';
import {
  WEATHER_CHART_CHROME_COLORS,
  WEATHER_CHART_PLOT_INTERACTION_PROPS,
  WEATHER_CHART_PLOT_HEIGHT,
  WEATHER_CHART_SHOW_BACKGROUND_GRADIENT,
  buildWeatherChartBands,
  createWeatherChartXTickFormatter,
  type WeatherChartBaseProps,
} from './chartDefaults';
import WeatherTheme from './weather-theme';

export interface WindChartProps extends WeatherChartBaseProps {
  directionSeries: EnrichedGraphSeriesPoint[];
}

const WindChart: FC<WindChartProps> = ({
  data,
  domain,
  timeZone,
  directionSeries,
  selectedTime,
  defaultSelectedTime,
  onSelectedTimeChange,
  onScrubStart,
  onScrubEnd,
}) => {
  const theme = useTheme();
  const unitsConfig = useSelector(selectUnitsConfig);

  const backgroundBands = useMemo(
    () => buildWeatherChartBands(domain, timeZone),
    [domain, timeZone],
  );

  const barColors = useMemo(
    () => buildBarChartColors(WeatherTheme.colors.wind, '#8FA8E8'),
    [],
  );

  const labelRows = useMemo(
    () => [
      buildWindDirectionLabelRow(directionSeries, WeatherTheme.colors.wind),
      buildWindSpeedValueLabelRow(data, unitsConfig),
    ],
    [data, directionSeries, unitsConfig],
  );

  const formatXTick = useMemo(
    () => createWeatherChartXTickFormatter(timeZone),
    [timeZone],
  );

  return (
    <TimeSeriesBarChartRecharts
      data={data}
      domain={domain}
      timeZone={timeZone}
      colors={barColors}
      plotHeight={WEATHER_CHART_PLOT_HEIGHT}
      xTickIntervalHours={3}
      hideMidnightTicks
      formatXTick={formatXTick}
      xTickColor={theme.colors.darkergrey}
      backgroundBands={backgroundBands}
      showBackgroundGradient={WEATHER_CHART_SHOW_BACKGROUND_GRADIENT}
      labelRows={labelRows}
      selectedTime={selectedTime}
      defaultSelectedTime={defaultSelectedTime}
      onSelectedTimeChange={onSelectedTimeChange}
      onScrubStart={onScrubStart}
      onScrubEnd={onScrubEnd}
      plotInteractionProps={WEATHER_CHART_PLOT_INTERACTION_PROPS}
      chromeColors={WEATHER_CHART_CHROME_COLORS}
    />
  );
};

export default WindChart;
